import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '../store'
import { submitSession, createSession, submitAssessment } from '../store/slices/testSlice'
import { testCompletionService } from '../services/testCompletion'
import { assessmentAPI } from '../services/api'
import toast from 'react-hot-toast'

interface WarningModalProps {
  isOpen: boolean
  warningCount: number
  onClose: () => void
  onConfirm: () => void
  isTabSwitch?: boolean
}

const WarningModal: React.FC<WarningModalProps> = ({ isOpen, warningCount, onClose, onConfirm, isTabSwitch = false }) => {
  if (!isOpen) return null

  const title = isTabSwitch ? 'Tab Switch Warning' : 'Navigation Warning'
  const icon = isTabSwitch ? (
    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  ) : (
    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
    </svg>
  )

  const message = isTabSwitch 
    ? 'You switched to another tab or window. This is not allowed during the test session.'
    : 'You are attempting to navigate away from the test. This is not allowed during the test session.'

  const stayButtonText = isTabSwitch ? 'Return to Test' : 'Stay on Test'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
            {icon}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">Warning {warningCount} of 3</p>
          </div>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            {message}
          </p>
          <p className="text-sm text-gray-600">
            {warningCount < 3 ? (
              <>After {3 - warningCount} more warning{warningCount === 2 ? '' : 's'}, your test will be automatically submitted.</>
            ) : (
              <span className="text-red-600 font-semibold">This is your final warning. Your test will be submitted immediately.</span>
            )}
          </p>
        </div>
        
        <div className="flex space-x-3">
          {/* Show "Return to Test" button only for warnings 1-2 */}
          {warningCount < 3 && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {stayButtonText}
            </button>
          )}
          
          {/* Show "Submit Test Now" button only for 3rd warning */}
          {warningCount >= 3 && (
            <button
              onClick={onConfirm}
              className="w-full px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
            >
              Submit Test Now
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export const useBackNavigationPrevention = (sessionId: string | undefined) => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { currentSession } = useSelector((state: RootState) => state.test)
  const { user } = useSelector((state: RootState) => state.auth)
  
  const [warningCount, setWarningCount] = useState(0)
  const [showWarning, setShowWarning] = useState(false)
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false)
  const isTestStarted = useRef(false)
  const isAutoSubmittingRef = useRef(false)
  const originalPushState = useRef<typeof history.pushState>()
  const originalReplaceState = useRef<typeof history.replaceState>()
  
  // Tab switch detection
  const [isTabSwitchWarning, setIsTabSwitchWarning] = useState(false)
  const tabSwitchTimeoutRef = useRef<NodeJS.Timeout>()
  const lastFocusTimeRef = useRef<number>(Date.now())
  const autoSubmitTimeoutRef = useRef<NodeJS.Timeout>()
  const lastWarningTimeRef = useRef<number>(0)

  // Simple check: if we're on a test page and session is started, activate prevention
  const shouldPreventNavigation = () => {
    const pathname = window.location.pathname
    const isTestQuestionPage = pathname.includes('/test/') && 
                              !pathname.includes('/instructions') &&
                              !pathname.includes('/system-check') &&
                              !pathname.includes('/completed') &&
                              !pathname.includes('/history')
    
    const isActive = currentSession?.status === 'started' && isTestQuestionPage
    console.log('shouldPreventNavigation:', { pathname, isTestQuestionPage, sessionStatus: currentSession?.status, isActive })
    return isActive
  }

  // Helper function to safely increment warning count with cooldown
  const incrementWarningCount = (source: string) => {
    const now = Date.now()
    const timeSinceLastWarning = now - lastWarningTimeRef.current
    
    // Prevent multiple warnings within 5 seconds
    if (timeSinceLastWarning < 5000) {
      console.log(`Warning from ${source} ignored - too soon after last warning (${timeSinceLastWarning}ms)`)
      return
    }
    
    setWarningCount(prev => {
      // Cap warning count at 3
      if (prev >= 3) {
        console.log(`Warning from ${source} ignored - already at max warnings (${prev})`)
        return prev
      }
      
      const newCount = prev + 1
      console.log(`Warning count incremented to ${newCount} by ${source}`)
      lastWarningTimeRef.current = now
      return newCount
    })
  }

  // Check if user is on actual test question page (not on pre-test or post-test pages)
  const isOnTestQuestionPage = () => {
    const pathname = window.location.pathname
    const isTestPage = pathname.includes('/test/') && 
                      !pathname.includes('/instructions') &&
                      !pathname.includes('/system-check') &&
                      !pathname.includes('/completed') &&
                      !pathname.includes('/history')
    
    console.log('isOnTestQuestionPage:', { pathname, isTestPage })
    return isTestPage
  }

  // Check if navigation target is allowed (within test flow)
  const isNavigationAllowed = (targetPath: string) => {
    // Allow navigation within test flow
    const allowedPaths = [
      '/instructions',
      '/system-check',
      '/completed',
      '/history'
    ]
    
    // Check if target is within test session flow
    const isWithinTestFlow = targetPath.includes('/test/')
    const isAllowedPage = allowedPaths.some(path => targetPath.includes(path))
    const isAllowed = isWithinTestFlow && (isAllowedPage || isOnTestQuestionPage())
    
    console.log('isNavigationAllowed:', { targetPath, isWithinTestFlow, isAllowedPage, isAllowed })
    return isAllowed
  }

  // Mark test as started when session is active and user is on test question page
  useEffect(() => {
    const shouldActivate = currentSession?.status === 'started' && isOnTestQuestionPage()
    console.log('Test activation check:', { 
      sessionStatus: currentSession?.status, 
      isOnTestPage: isOnTestQuestionPage(), 
      shouldActivate,
      sessionId 
    })
    
    isTestStarted.current = shouldActivate
  }, [currentSession?.status, sessionId])

  // Listen for path changes to dynamically update test status
  useEffect(() => {
    const handlePathChange = () => {
      const shouldActivate = currentSession?.status === 'started' && isOnTestQuestionPage()
      console.log('Path change - updating test status:', { shouldActivate })
      isTestStarted.current = shouldActivate
    }

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handlePathChange)
    
    return () => {
      window.removeEventListener('popstate', handlePathChange)
    }
  }, [currentSession?.status])

  // Prevent back navigation and other navigation attempts
  useEffect(() => {
    if (!sessionId) return

    const isActive = shouldPreventNavigation()
    console.log('Navigation prevention effect:', { 
      isActive, 
      sessionId,
      currentPath: window.location.pathname,
      sessionStatus: currentSession?.status
    })
    
    if (!isActive) {
      console.log('Navigation prevention not active - skipping setup')
      return
    }
    
    console.log('Setting up navigation prevention...')

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'Are you sure you want to leave? Your test may be submitted.'
      return 'Are you sure you want to leave? Your test may be submitted.'
    }

    const handlePopState = (event: PopStateEvent) => {
      console.log('Popstate event triggered:', { warningCount, targetPath: window.location.pathname })
      
      if (warningCount >= 3) {
        console.log('Max warnings reached - auto submitting')
        handleAutoSubmit()
        return
      }
      
      // Simple check: if target is not within test flow, show warning
      const targetPath = window.location.pathname
      const isWithinTestFlow = targetPath.includes('/test/')
      
      if (!isWithinTestFlow) {
        console.log('Navigation outside test flow - showing warning')
        event.preventDefault()
        setShowWarning(true)
        // Increment warning count when showing the warning
        incrementWarningCount('navigation-popstate')
        return
      }
      
      console.log('Navigation within test flow - allowing')
    }

    // Override browser navigation methods
    const overridePushState = (originalMethod: typeof history.pushState) => {
      return function(this: History, ...args: Parameters<typeof history.pushState>) {
        if (warningCount >= 3) {
          handleAutoSubmit()
          return
        }
        
        // Check if target URL is allowed
        const targetUrl = args[2] as string
        if (targetUrl && isNavigationAllowed(targetUrl)) {
          // Allow navigation to declaration/system-check pages
          return originalMethod.apply(this, args)
        }
        
        setShowWarning(true)
      }
    }

    const overrideReplaceState = (originalMethod: typeof history.replaceState) => {
      return function(this: History, ...args: Parameters<typeof history.replaceState>) {
        if (warningCount >= 3) {
          handleAutoSubmit()
          return
        }
        
        // Check if target URL is allowed
        const targetUrl = args[2] as string
        if (targetUrl && isNavigationAllowed(targetUrl)) {
          // Allow navigation to declaration/system-check pages
          return originalMethod.apply(this, args)
        }
        
        setShowWarning(true)
      }
    }

    // Store original methods
    originalPushState.current = history.pushState
    originalReplaceState.current = history.replaceState

    // Override navigation methods
    history.pushState = overridePushState(history.pushState)
    history.replaceState = overrideReplaceState(history.replaceState)

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    return () => {
      // Restore original methods
      if (originalPushState.current) {
        history.pushState = originalPushState.current
      }
      if (originalReplaceState.current) {
        history.replaceState = originalReplaceState.current
      }
      
      // Cleanup timeouts
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
        autoSubmitTimeoutRef.current = undefined
      }
      
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [sessionId, warningCount, currentSession?.status])

  // Tab switch detection - only active during test questions
  useEffect(() => {
    if (!sessionId) return

    const isActive = shouldPreventNavigation()
    const disableTabSwitchWarnings = currentSession?.disable_tab_switch_warnings
    console.log('Tab switch detection effect:', { isActive, disableTabSwitchWarnings })

    if (!isActive) {
      return
    }

    // If tab switch warnings are disabled for this session (e.g., AI/ML Assignment), skip all detection
    if (disableTabSwitchWarnings) {
      console.log('Tab switch warnings disabled for this session - skipping tab switch detection')
      return
    }

    const handleVisibilityChange = () => {
      console.log('Visibility change detected:', { hidden: document.hidden })
      
      // Double-check the flag in case it changed
      if (currentSession?.disable_tab_switch_warnings) {
        console.log('Tab switch warnings disabled - ignoring visibility change')
        return
      }
      
      if (document.hidden) {
        // Tab/window lost focus
        console.log('Tab lost focus - starting detection')
        lastFocusTimeRef.current = Date.now()
        
        // Set a timeout to detect if tab stays hidden for too long
        tabSwitchTimeoutRef.current = setTimeout(() => {
          console.log('Tab switch timeout reached - showing warning')
          setIsTabSwitchWarning(true)
          setShowWarning(true)
          // Increment warning count when showing the warning
          incrementWarningCount('tab-switch-timeout')
        }, 2000) // 2 seconds tolerance
      } else {
        // Tab/window gained focus
        console.log('Tab gained focus')
        
        // Clear timeout if tab returns quickly
        if (tabSwitchTimeoutRef.current) {
          clearTimeout(tabSwitchTimeoutRef.current)
          tabSwitchTimeoutRef.current = undefined
        }
        
        // Check if tab was away for too long
        const timeAway = Date.now() - lastFocusTimeRef.current
        console.log('Time away from tab:', timeAway, 'ms')
        
        if (timeAway > 3000) { // 3 seconds threshold
          console.log('Tab was away too long - showing warning')
          setIsTabSwitchWarning(true)
          setShowWarning(true)
          // Increment warning count when showing the warning
          incrementWarningCount('tab-switch-return')
        }
      }
    }

    const handleFocus = () => {
      console.log('Window focus gained')
      
      // Check if warnings are disabled
      if (currentSession?.disable_tab_switch_warnings) {
        console.log('Tab switch warnings disabled - ignoring focus gain')
        return
      }
      
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current)
        tabSwitchTimeoutRef.current = undefined
      }
    }

    const handleBlur = () => {
      console.log('Window focus lost')
      
      // Check if warnings are disabled
      if (currentSession?.disable_tab_switch_warnings) {
        console.log('Tab switch warnings disabled - ignoring focus loss')
        return
      }
      
      lastFocusTimeRef.current = Date.now()
      
      tabSwitchTimeoutRef.current = setTimeout(() => {
        console.log('Window blur timeout - showing warning')
        setIsTabSwitchWarning(true)
        setShowWarning(true)
        // Increment warning count when showing the warning
        incrementWarningCount('window-blur')
      }, 2000)
    }

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      // Cleanup
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [sessionId, currentSession?.status, currentSession?.disable_tab_switch_warnings])

  const handleAutoSubmit = async () => {
    if (isAutoSubmittingRef.current) return
    isAutoSubmittingRef.current = true
    
    try {
      setIsAutoSubmitting(true)
      toast.error('Test automatically submitted due to multiple navigation attempts')
      
      console.log('[useBackNavigationPrevention] Auto-submitting test session:', sessionId)
      
      // Submit using new assessment responses system (only if we have a valid drive_id)
      const candidateId = user?.id
      const driveId = currentSession?.drive_id ? parseInt(String(currentSession.drive_id), 10) : null
      const assessmentId = driveId && currentSession?.assessment_type 
        ? `${driveId}_${currentSession.assessment_type}` 
        : null

      if (driveId !== null && candidateId && assessmentId) {
        // Submit the assessment
        try {
          await dispatch(submitAssessment({
            assessmentId,
            candidateId,
            driveId,
            submissionReason: 'tab_limit'
          })).unwrap()
        } catch (error) {
          console.error('[useBackNavigationPrevention] Error submitting assessment:', error)
          throw error
        }
      }

      await dispatch(submitSession({ sessionId: sessionId || '', reason: 'tab_limit' })).unwrap()
      
      // Mark assessment as completed (same as regular submit)
      if (currentSession && user?.email) {
        console.log('[useBackNavigationPrevention] Marking assessment as completed for auto-submit')
        testCompletionService.markAssessmentCompleted({
          assessmentId: currentSession.assessment_id || 0,
          assessmentTitle: currentSession.assessment_title || 'Assessment',
          assessmentType: currentSession.assessment_type || 'unknown',
          driveId: currentSession.drive_id || '',
          driveTitle: currentSession.drive_title || 'Drive',
          sessionId: sessionId || '',
          candidateEmail: user.email,
          durationMinutes: currentSession.duration_minutes || 0
        })
      }
      
      // Check if there are more assessments in the drive sequence
      const assessmentSequenceStr = sessionStorage.getItem('currentDriveAssessmentSequence')
      const currentIndexStr = sessionStorage.getItem('currentDriveAssessmentIndex')
      
      console.log('[useBackNavigationPrevention] Checking for next assessment after auto-submit:', {
        hasSequence: !!assessmentSequenceStr,
        hasIndex: !!currentIndexStr,
        currentIndexStr
      })
      
      if (assessmentSequenceStr && currentIndexStr) {
        try {
          const assessmentSequence = JSON.parse(assessmentSequenceStr)
          const currentIndex = parseInt(currentIndexStr, 10)
          const nextIndex = currentIndex + 1
          
          console.log('[useBackNavigationPrevention] Assessment sequence check:', {
            sequenceLength: assessmentSequence.length,
            currentIndex,
            nextIndex,
            hasNext: nextIndex < assessmentSequence.length
          })
          
          if (nextIndex < assessmentSequence.length) {
            // More assessments remaining - start next one
            console.log('[useBackNavigationPrevention] Starting next assessment after auto-submit')
            toast.success('Assessment submitted! Starting next assessment...', {
              duration: 3000
            })
            
            // Update current index in sessionStorage
            sessionStorage.setItem('currentDriveAssessmentIndex', String(nextIndex))
            
            // Get the next assessment
            const nextAssessment = assessmentSequence[nextIndex]
            
            // Generate questions for the next assessment
            const separator = nextAssessment.id.indexOf('_')
            const driveIdFromSeq = nextAssessment.id.slice(0, separator)
            const assessmentType = nextAssessment.id.slice(separator + 1)
            const numQuestions = nextAssessment.numQuestions || 1
            const durationMinutes = nextAssessment.duration_minutes || 30
            
            const questionsResponse = await assessmentAPI.generateQuestions(
              driveIdFromSeq,
              assessmentType,
              numQuestions,
              durationMinutes
            )
            
            const generatedQuestions = questionsResponse.data.questions
            
            // Create new session for next assessment
            const nextSessionResponse = await dispatch(createSession({
              assessment_id: nextAssessment.id,
              questions: generatedQuestions,
              duration_minutes: durationMinutes,
              assessment_type: assessmentType,
              candidate_email: user?.email
            }))
            
            if (nextSessionResponse.payload) {
              const nextSession = nextSessionResponse.payload as any
              console.log('[useBackNavigationPrevention] Next assessment session created, navigating to instructions')
              navigate(`/test/${nextSession.session_id}/instructions`, { replace: true })
              return
            }
          } else {
            // All assessments completed
            console.log('[useBackNavigationPrevention] All assessments completed after auto-submit')
            // Clear sessionStorage
            const currentDriveId = sessionStorage.getItem('currentDriveId')
            sessionStorage.removeItem('currentDriveAssessmentSequence')
            sessionStorage.removeItem('currentDriveAssessmentIndex')
            sessionStorage.removeItem('currentDriveId')
            sessionStorage.removeItem('assessmentSequenceStarted')
            // Clear the "left" flag if it exists (since all assessments were completed normally)
            if (currentDriveId) {
              sessionStorage.removeItem(`drive_${currentDriveId}_left`)
            }
            navigate(`/test/${sessionId}/completed`, { replace: true })
            return
          }
        } catch (error) {
          console.error('[useBackNavigationPrevention] Error processing next assessment after auto-submit:', error)
          // Fall through to navigate to completed page
        }
      }
      
      // No sequence or error - navigate to completed page
      console.log('[useBackNavigationPrevention] No assessment sequence found, navigating to completed page')
      navigate(`/test/${sessionId}/completed`, { replace: true })
    } catch (error) {
      console.error('[useBackNavigationPrevention] Failed to auto-submit test:', error)
      toast.error('Failed to submit test automatically')
      // Keep the candidate on this page until the server confirms submission.
      toast.error('Submission is not confirmed. Please retry Submit Test Now.')
    } finally {
      setIsAutoSubmitting(false)
      isAutoSubmittingRef.current = false
    }
  }

  const handleWarningConfirm = async () => {
    // Warning count is already incremented when warning is shown
    setShowWarning(false)
    setIsTabSwitchWarning(false)
    
    if (warningCount >= 3) {
      // Force auto-submit immediately on 3rd warning
      console.log('3rd warning reached - forcing auto-submit')
      await handleAutoSubmit()
    } else {
      const warningType = isTabSwitchWarning ? 'tab switching' : 'navigation away from test'
      toast.error(`Warning ${warningCount}/3: ${warningType} is not allowed`)
    }
  }

  const handleWarningClose = () => {
    setShowWarning(false)
    setIsTabSwitchWarning(false)
    
    // For navigation warnings, push current state to prevent navigation
    if (!isTabSwitchWarning && originalPushState.current) {
      originalPushState.current.call(history, history.state, '', window.location.href)
    }
  }

  // Failsafe: Auto-submit when warning count reaches 3
  useEffect(() => {
    if (warningCount >= 3 && !isAutoSubmittingRef.current) {
      console.log('Failsafe triggered - auto-submitting due to 3 warnings')
      
      // Set a timeout to auto-submit after 10 seconds even if user doesn't click
      autoSubmitTimeoutRef.current = setTimeout(() => {
        console.log('Auto-submit timeout reached - forcing submission')
        handleAutoSubmit()
      }, 10000) // 10 seconds timeout
      
      handleAutoSubmit()
    }
    
    // Cleanup timeout when warning count changes
    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
        autoSubmitTimeoutRef.current = undefined
      }
    }
  }, [warningCount])

  const WarningModalComponent = () => (
    <WarningModal
      isOpen={showWarning}
      warningCount={warningCount}
      onClose={handleWarningClose}
      onConfirm={handleWarningConfirm}
      isTabSwitch={isTabSwitchWarning}
    />
  )

  return {
    WarningModalComponent,
    isAutoSubmitting
  }
}
